import API from "../../api/api";
import moment from "moment";
import { PATHOLOGY_SUMMARY_ENABLED } from "../../utilities/feature-flags";

const $API = new API();

let _pendingPollTimers = [];

const TableDocs = {
  state: () => ({
    docsEvents: [],
    pendingSummaryIds: [],
    docsTypes: {
      pathology: ["pathological-report"],
      genetics: ["pgd-lab-results", "dnachip-lab-results", "exome-lab-results"]
    }
  }),
  getters: {
    docsTypes(state) {
      return state.docsTypes;
    },
    defaultDates() {
      return {
        startDate: moment(new Date())
          .subtract(3, "week")
          .format("YYYY-MM-DDT00:00:00"),
        endDate: moment().format("YYYY-MM-DDT00:00:00")
      };
    },
    eventsByType(state) {
      const events = {};
      if (!state.docsEvents?.length) return events;

      for (const key in state.docsTypes) {
        events[key] = state.docsEvents.filter(
          e =>
            e.file &&
            state.docsTypes[key].some(
              type => e.file.path.includes(type) || e.file.type.includes(type)
            )
        );
      }
      return events;
    },
    hasUnwatchedDocs: (state, getters) => (type = "pathology") => {
      if (!getters.eventsByType[type]) return false;
      return getters.eventsByType[type].some(e => e.count == 0);
    }
  },
  mutations: {
    ADD_PENDING_SUMMARY(state, id) {
      if (!state.pendingSummaryIds.includes(id)) {
        state.pendingSummaryIds.push(id);
      }
    },
    REMOVE_PENDING_SUMMARY(state, id) {
      const idx = state.pendingSummaryIds.indexOf(id);
      if (idx !== -1) state.pendingSummaryIds.splice(idx, 1);
    },
    SET_DOCS_LIST(state, events) {
      state.docsEvents = events.map(e => {
        const id = e.file && e.file.path ? e.file.path : "";
        const count = e.doctor_view_count
          ? e.doctor_view_count.doctor_view_count
          : 0;
        const is_available_online =
          e?.document_status?.document_status === "פעיל וזמין למטופל"
            ? "זמין"
            : "לא זמין";
        let located_report = "לא אותר";
        let has_pathology_summary = false;
        let pathology_summary_path = "";
        let pathology_summary_file = "";
        // Pathology report rows: locate matching summary document for the same patient
        if (
          e.file &&
          state.docsTypes.pathology.some(
            type => e.file.path.includes(type) || e.file.type.includes(type)
          )
        ) {
          const foundReport = events.find(ev => {
            return (
              ev.real_patient?.real_patient_id ===
                e.real_patient?.real_patient_id &&
              (ev.file.description === "סיכום תוצאות פתולוגיה" ||
                ev.file.path.includes("-70."))
            );
          });
          if (foundReport) {
            const count = foundReport.view_count?.view_count;
            located_report = count && parseInt(count) > 0 ? "צפה" : "לא צפה";
            if (PATHOLOGY_SUMMARY_ENABLED && foundReport.file?.path) {
              has_pathology_summary = true;
              pathology_summary_path =
                "https://md-booking.hmc.co.il/ex-doctor/external" +
                foundReport.file.path;
              pathology_summary_file =
                foundReport.file.file_name ||
                foundReport.file.description ||
                "סיכום תוצאות פתולוגיה";
            }
          }
        }
        return {
          ...e,
          id,
          count,
          located_report,
          is_available_online,
          has_pathology_summary,
          pathology_summary_path,
          pathology_summary_file
        };
      });

      if (PATHOLOGY_SUMMARY_ENABLED) {
        state.pendingSummaryIds = state.pendingSummaryIds.filter(
          pid =>
            !state.docsEvents.some(
              ev => ev.id === pid && ev.has_pathology_summary
            )
        );
      }
    },
    UPDATE_FILE_WATCHED(state, filePath) {
      const path = filePath.replace(
        "https://md-booking.hmc.co.il/ex-doctor/external",
        ""
      );
      const i = state.docsEvents.findIndex(e => e.id == path);
      if (i === -1) return;
      state.docsEvents[i].count++;
    }
  },
  actions: {
    async GET_DOCS_LIST({ commit, getters, rootGetters }, newDates) {
      const doctorId = rootGetters.activeDoctor.id;
      const dates = newDates ?? getters.defaultDates;

      if (!doctorId) return [];
      const res = await $API.getTableDocsList({
        doctorId,
        fromDate: moment(dates.startDate, "YYYY-MM-DDThh:mm:ss").format(
          "YYYY-MM-DD"
        ),
        toDate: moment(dates.endDate, "YYYY-MM-DDThh:mm:ss").format(
          "YYYY-MM-DD"
        )
      });
      if (!res || !res.data || !res.data.data) return [];

      commit("SET_DOCS_LIST", res.data.data);
      return res.data.data;
    },
    POLL_PENDING_SUMMARIES({ state, dispatch }) {
      _pendingPollTimers.forEach(t => clearTimeout(t));
      _pendingPollTimers = [];

      if (!PATHOLOGY_SUMMARY_ENABLED || !state.pendingSummaryIds.length) return;

      const retry = delay => {
        const timer = setTimeout(async () => {
          if (!state.pendingSummaryIds.length) return;
          await dispatch("GET_DOCS_LIST");
        }, delay);
        _pendingPollTimers.push(timer);
      };

      retry(30000);
      retry(60000);
    },
    CANCEL_PENDING_POLL() {
      _pendingPollTimers.forEach(t => clearTimeout(t));
      _pendingPollTimers = [];
    }
  }
};

export default TableDocs;
